import { ArrowLeft, Flame } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  FUELS_CONDITIONS,
  FUELS_MODEL_BOUNDARIES,
  FUELS_MODES,
  FUELS_SOURCES,
  FUELS_SUSTAINABILITY_TOOL_IDENTITY,
  FUELS_YEAR_MODES,
  type FuelsMode,
} from './fuelsSustainability';
import { FuelsSustainabilityEmissions } from './FuelsSustainabilityEmissions';
import { FuelsSustainabilityFossilBio } from './FuelsSustainabilityFossilBio';
import { FuelsSustainabilityProduction } from './FuelsSustainabilityProduction';
import { StudyShell } from './StudyShell';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';

const MODE_COPY: Record<FuelsMode, { tab: string; eyebrow: string; title: string; description: string }> = {
  'fossil-bio': {
    tab: 'Fossil or bio',
    eyebrow: 'Compare fuels',
    title: 'Where does the carbon come from, and how much CO₂ does each MJ cost?',
    description:
      'Predict whether a fuel is renewable and how its exhaust CO₂ compares with natural gas, then check the model.',
  },
  production: {
    tab: 'Production',
    eyebrow: 'Make the fuel',
    title: 'Balance the equations that make bioethanol, biodiesel, biogas and hydrogen',
    description:
      'Change coefficients, watch the atom count, and see the typical conditions once the equation balances.',
  },
  emissions: {
    tab: 'Emissions',
    eyebrow: 'Count the CO₂',
    title: 'Calculate CO₂ from a balanced equation and test carbon-neutral claims',
    description:
      'Use mole ratios to find CO₂ mass and CO₂ per MJ, then decide what the chemistry can and cannot prove.',
  },
};

export function FuelsSustainabilityLabPage() {
  const year = useChemistryToolYear();
  const [mode, setMode] = useState<FuelsMode>(() => modeForYear(FUELS_YEAR_MODES, year));
  const copy = MODE_COPY[mode];

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'fuels-sustainability', state: { mode } }}>
      <Link
        to="/learn/study/subjects/chemistry/tools"
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry tools
      </Link>

      <header
        className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-coral/25 bg-gradient-to-br from-wash-coral via-canvas-pure to-wash-mint p-6 shadow-card-soft lg:p-8"
        data-tool-key={FUELS_SUSTAINABILITY_TOOL_IDENTITY.toolKey}
        data-tool-version={FUELS_SUSTAINABILITY_TOOL_IDENTITY.toolVersion}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-coral">
              <Flame className="h-4 w-4" aria-hidden="true" /> Energy and thermochemistry
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Fuels &amp; Sustainability Lab
            </h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              Compare fossil fuels and biofuels, balance the equations that make them, and count
              the CO₂ they release.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            Year 11–12 · senior chemistry
          </div>
        </div>

        <div
          className="mt-6 grid max-w-xl grid-cols-3 rounded-full bg-white/75 p-1"
          role="group"
          aria-label="Choose a mode"
        >
          {FUELS_MODES.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={mode === option}
              onClick={() => setMode(option)}
              className={`rounded-full px-2 py-2.5 text-sm font-extrabold transition-colors ${
                mode === option ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white'
              }`}
            >
              {MODE_COPY[option].tab}
            </button>
          ))}
        </div>
      </header>

      <section className="my-6 rounded-[1.75rem] border border-hairline bg-canvas-pure px-5 py-4 shadow-card-soft sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
              {copy.eyebrow}
            </p>
            <h2 className="mt-1 text-xl font-extrabold text-ink">{copy.title}</h2>
            <p className="mt-1 text-sm font-medium leading-6 text-slate2">{copy.description}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2 rounded-full bg-wash-sunshine px-4 py-2 text-xs font-extrabold text-ink-soft">
            Explore only · not mastery evidence
          </div>
        </div>
      </section>

      {mode === 'fossil-bio' ? <FuelsSustainabilityFossilBio /> : null}
      {mode === 'production' ? <FuelsSustainabilityProduction /> : null}
      {mode === 'emissions' ? <FuelsSustainabilityEmissions /> : null}

      <section
        className="mt-6 rounded-3xl border border-brand-sunshine/40 bg-wash-sunshine p-5"
        aria-label="Model boundaries"
      >
        <p className="text-sm font-extrabold text-ink">Model boundaries</p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm font-medium leading-6 text-ink-soft">
          {FUELS_MODEL_BOUNDARIES.map((boundary) => (
            <li key={boundary}>{boundary}</li>
          ))}
        </ul>
        <p className="mt-3 text-xs font-bold leading-5 text-slate2">
          {FUELS_CONDITIONS} Sources: {FUELS_SOURCES.nist.label}; {FUELS_SOURCES.iupac.label};{' '}
          {FUELS_SOURCES.processes.label}. Values are pending teaching review.
        </p>
      </section>

      <footer className="my-6 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-xs font-medium leading-5 text-slate2">
        This tool uses reviewed, bounded teaching cases. It is not a life-cycle assessment, does not
        replace a supervised practical or risk assessment, saves no attempt and does not claim
        alignment to a state syllabus.
      </footer>
    </StudyShell>
  );
}
